import React, { FC, PropsWithChildren, useState, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import greyDialogTitleSx from './greyDialogTitleSx';
// material-ui
import {
  Box,
  Grid,
  Typography,
  SelectChangeEvent,
  Button,
  Select,
  Checkbox,
  MenuItem,
  ListItemText,
  TextField,
} from "@mui/material";
import {
  IAccountBasicFormInputs,
} from "../../utils/account.types";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { StyledInput } from './Dialog.styles';
import { IOptions } from "../FormFields/CustomSelect";
import { industryVerticalList } from './DialogForm';
import LoadingButton from "@mui/lab/LoadingButton";
import { ApiStatus } from "../../utils/api.types";
import { useAppSelector } from "../../store/hooks";

export interface IDialogProps {
  title: string;
  open: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  isAddButton?: boolean;
  data?: any;
}

// Schema to validate account form.
const schema = yup.object().shape({
  name: yup.string().required("Name is required."),
  legalName: yup.string().required("Legal Name is required."),
  taxIdentificationNumber1: yup.string().required("Tax Identification #1 (PAN for India) is required."),
  taxIdentificationNumber2: yup.string().required("Tax Identification #2 (GST for India) is required."),
});

//Custom Dialog Component.
const CustomDialog: FC<PropsWithChildren<IDialogProps>> = ({
  title,
  onClose,
  open,
  children,
  onSubmit,
  isAddButton,
  data,
}) => {
  const [goToPlans, setGoToPlans] = useState(false);
  const [industryVerticalOptions] = useState<IOptions[]>(industryVerticalList);
  const [selectedIndustryVertical, setSelectedIndustryVertical] = useState('');
  // const [selectedCompanyType, setSelectedCompanyType] = useState("");
  //const [companyTypeOptions, setCompanyTypeOptions] = useState<IOptions[]>([]);
  const { status } = useAppSelector((state) => state.account);
  const [image, setImage] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedBusinessType, setSelectedBusinessType] = useState<string[]>([]);

  const businessTypeOptions = [
    'Product',
    'Service',
    'Consulting'
  ];

  // use form
  const methods = useForm<IAccountBasicFormInputs>({
    resolver: yupResolver(schema),
  });

  // Method to set preview of selected picture.
  const handleSelectFile = (event: any) => {
    const target = event.target as HTMLInputElement;

    if (target.files && target.files[0]) {
      setSelectedFile(target.files[0]);
      setImage(URL.createObjectURL(target.files[0]));
    }
  };

  const innerSubmit = async (data: any) => {
    const form = new FormData();
    form.append('name', data.name);
    form.append('legalName', data.legalName);
    form.append('industryVertical', data.industryVertical);
    form.append('displayName', data.displayName);
    form.append('companyDesc', data.companyDesc);
    form.append('businessType', JSON.stringify(selectedBusinessType));
    form.append('taxIdentificationNumber1', data.taxIdentificationNumber1);
    form.append('taxIdentificationNumber2', data.taxIdentificationNumber2);
    if (selectedFile) {
      form.append('logo', selectedFile);
    }
    if (data.capitalOpeningBalance) {
      form.append('capitalOpeningBalance', data.capitalOpeningBalance);
    }
    //onSubmit({...data, ...{subscription: subscriptionPlan?.value, logo: form }});
    onSubmit(form);
  };

  const handleSelectChange = (
    event: SelectChangeEvent<typeof selectedBusinessType>
  ) => {
    const {
      target: { value },
    } = event;
    setSelectedBusinessType(
      typeof value === "string" ? value.split(",") : value
    );
  };

  useEffect(() => {
    let basicInfoAvaliable = true;

    const setInitialData = () => {
      try {
        if (data && basicInfoAvaliable) {
          methods.setValue('name', data.name);
          methods.setValue('displayName', data.displayName);
          methods.setValue('companyDesc', data.companyDesc);
          methods.setValue('website', data.website);
          methods.setValue('taxIdentificationNumber1', data.taxIdentificationNumber1);
          methods.setValue('taxIdentificationNumber2', data.taxIdentificationNumber2);
          if (data.businessType) setSelectedBusinessType(data.businessType);
          if (data.industryVertical) setSelectedIndustryVertical(data.industryVertical);
          //setSelectedCompanyType(data.companyType);
          setImage(data.logo);
        }
      } catch (error: any) {
        console.log("Dialog catch block called");
      }
    };

    setInitialData();

    return () => {
      basicInfoAvaliable = false;
    };
  }, [data, methods]);

  const gotoPlan = () => {
    let brangName = methods.getValues('name');
    let legalName = methods.getValues('legalName');
    let taxIdentificationNumber1 = methods.getValues('taxIdentificationNumber1');
    let taxIdentificationNumber2 = methods.getValues('taxIdentificationNumber2');
    if(!brangName) methods.trigger("name");
    if(!legalName) methods.trigger("legalName");
    if(!taxIdentificationNumber1) methods.trigger("taxIdentificationNumber1");
    if(!taxIdentificationNumber2) methods.trigger("taxIdentificationNumber2");
    if (brangName && legalName && taxIdentificationNumber1 && taxIdentificationNumber2) {
      setGoToPlans(true);
    }else{
      methods.handleSubmit(innerSubmit);
    }
  };


  const inputSx = { width: '100%', mt: 0.5, borderRadius: '8px!important' };
  const selectSx = {
    width: '100%',
    mt: 0.5,
    backgroundColor: '#fff',
    borderRadius: '10px',
    padding: '8px 12px',
    '& .MuiSelect-select': { padding: '0', display: 'flex', alignItems: 'center' },
  };

  const textField = (name: keyof IAccountBasicFormInputs, label: string, placeholder: string, extra: any = {}) => (
    <>
      <h5 className='c-label'>{label}</h5>
      <TextField
        placeholder={placeholder}
        {...methods.register(name)}
        size='small'
        className='c-text-field-4'
        variant="standard"
        sx={inputSx}
        InputProps={{ disableUnderline: true }}
        {...extra}
      />
      {(methods.formState.errors as any)[name]?.message &&
        <p style={{ color: "#c33b3b", fontSize: '12px', margin: '4px 0 0 0' }}>{(methods.formState.errors as any)[name]?.message}</p>}
    </>
  );

  const section = (heading: string) => (
    <Grid item xs={12}>
      <Box sx={{ borderBottom: '1px solid #e4e4e4', pb: 1, mb: 0.5 }}>
        <Typography sx={{ fontWeight: 600, fontSize: '15px', color: '#1a1a1a' }}>{heading}</Typography>
      </Box>
    </Grid>
  );

  return (
    <Dialog
      open={open}
      fullWidth={true}
      maxWidth={(!goToPlans)? "lg" : "md"}
      PaperProps={!goToPlans ? { sx: { width: '100%', maxWidth: '1000px', maxHeight: '92vh' } } : undefined}
      onClose={isAddButton ? onClose : undefined}
    >
      <FormProvider {...methods}>
        <Box
          component="form"
          onSubmit={methods.handleSubmit(innerSubmit)}
          noValidate
          sx={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}
        >
          <DialogTitle sx={greyDialogTitleSx}>
            <Typography variant="h5">{title}</Typography>
          </DialogTitle>

          <DialogContent sx={{ flex: 1, overflow: "auto", px: 3, pt: '20px !important', pb: 3 }}>
            <Grid container columnSpacing={2.5} rowSpacing={2}>
              {!goToPlans ? (
                <>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ border: '1px solid #dcdcdc', borderRadius: '8px', p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ width: 120, height: 80, flexShrink: 0, borderRadius: '6px', bgcolor: '#f5f6f8', border: '1px dashed #cfd4dc', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                        {image
                          ? <img src={image} alt="Logo" referrerPolicy="no-referrer" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                          : <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>No logo</Typography>}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#1a1a1a', mb: 1 }}>Logo</Typography>
                        <label htmlFor="logo">
                          <StyledInput accept="image/*" id="logo" type="file" onChange={handleSelectFile} />
                          <Button variant="outlined" component="span" size="small" sx={{ textTransform: 'none' }}>
                            {image ? 'Change Logo' : 'Upload Logo'}
                          </Button>
                        </label>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sx={{ mt: 1 }} />
                  {section('Business Information')}
                  <Grid item xs={12} md={4}>{textField('name', 'Brand Name *', 'Brand Name')}</Grid>
                  <Grid item xs={12} md={4}>{textField('displayName', 'Display Name', 'Display Name')}</Grid>
                  <Grid item xs={12} md={4}>{textField('legalName', 'Legal/Business Name *', 'Legal Name')}</Grid>
                  <Grid item xs={12} md={4}>
                    <h5 className='c-label'>Business Type</h5>
                    <Select
                      multiple
                      value={selectedBusinessType}
                      onChange={handleSelectChange}
                      displayEmpty
                      renderValue={(selected: any) => selected.length ? selected.join(", ") : <span style={{ color: '#9e9e9e' }}>Select business type</span>}
                      size='small'
                      variant='outlined'
                      sx={selectSx}
                    >
                      {businessTypeOptions.map((name) => (
                        <MenuItem key={name} value={name}>
                          <Checkbox checked={selectedBusinessType.indexOf(name) > -1} size="small" />
                          <ListItemText primary={name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <h5 className='c-label'>Industry Vertical</h5>
                    <Select
                      {...methods.register('industryVertical')}
                      value={selectedIndustryVertical}
                      onChange={(event: SelectChangeEvent<any>) => {
                        setSelectedIndustryVertical(event.target.value);
                      }}
                      displayEmpty
                      renderValue={(value: any) => value || <span style={{ color: '#9e9e9e' }}>Select industry</span>}
                      size='small'
                      sx={selectSx}
                    >
                      {industryVerticalOptions.map((option) =>
                        <MenuItem value={option.value} key={option.value}>{option.label}</MenuItem>
                      )}
                    </Select>
                  </Grid>
                  <Grid item xs={12} md={4} />
                  <Grid item xs={12}>{textField('companyDesc', 'Company Description', 'Company Description', { multiline: true, rows: 2 })}</Grid>

                  <Grid item xs={12} sx={{ mt: 1 }} />
                  {section('Tax Information')}
                  <Grid item xs={12} md={4}>{textField('taxIdentificationNumber1', 'Tax Id #1 (PAN for India) *', 'PAN Number')}</Grid>
                  <Grid item xs={12} md={4}>{textField('taxIdentificationNumber2', 'Tax Id #2 (GST for India) *', 'GST Number', { inputProps: { style: { textTransform: 'uppercase' } } })}</Grid>

                  <Grid item xs={12} sx={{ mt: 1 }} />
                  {section('Opening Balance')}
                  <Grid item xs={12} md={4}>{textField('capitalOpeningBalance', 'Capital Opening Balance', '0.00', { type: 'number', inputProps: { min: 0 } })}</Grid>
                </>
              ) : (
                <Grid item xs={12}>
                  {/* plans are assigned by the server: every new business starts on a free trial */}
                  <Box sx={{ p: 3, backgroundColor: "#fafafb", border: '1px solid #e4e4e4', borderRadius: "8px" }}>
                    <Typography sx={{ fontWeight: 600, fontSize: 16, mb: 1 }}>Your free trial</Typography>
                    <Typography sx={{ color: '#6b7280' }}>
                      Your business starts with a free 14-day trial of the Business plan. After the trial it moves to the free Starter plan unless you upgrade. You can see your plan any time in My Business Information → Subscription.
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
            {children}
          </DialogContent>

          <DialogActions sx={{ justifyContent: 'flex-end' }}>
            {!goToPlans ? (
              <>
                {isAddButton && (
                  <Button variant="outlined" onClick={() => { methods.reset(); onClose(); }}>
                    Cancel
                  </Button>
                )}
                <Button variant="contained" onClick={gotoPlan}>
                  Next
                </Button>
              </>
            ) : (
              <>
                <Button variant="outlined" onClick={() => setGoToPlans(false)}>
                  Back
                </Button>
                <LoadingButton type="submit" loading={status === ApiStatus.LOADING} variant="contained">
                  {data ? "Update" : "Create"}
                </LoadingButton>
              </>
            )}
          </DialogActions>
        </Box>
      </FormProvider>
    </Dialog>
  );
};

export default CustomDialog;
